import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useToken } from "../Auth/useToken";
import { useOrganisations } from "../Auth/useOrganisations";
import { useActiveJournal } from "../Auth/useActiveJournal";
import { login } from "../Services/UserApi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getUserOrganisations } from "../Services/OrganizationApi";
import { Modal, Box, Typography, TextField, Button } from "@mui/material";
import { updateCashJournal } from "../Services/JournalApi";
import { ImpressionTicket } from "../Helpers/Helpers";
import iconv from "iconv-lite"; // npm i iconv-lite
import { PrintAction } from "../Shared/Hardware/PrintActions";
import { useCaisse } from "../Auth/useCaisse";
import { getCaisseFromStorage } from "../Services/CaisseApi";
import KeyboardComponent from "../Components/Caisse/keyboards/virtualKeypad";

const isElectron = navigator.userAgent.toLowerCase().includes("electron");

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [isBadgeMode, setIsBadgeMode] = useState(false);
  const [showLetters, setShowLetters] = useState(false);
  const [showFundModal, setShowFundModal] = useState(false);
  const [fondInitial, setFondInitial] = useState("");
  const [pendingLoginData, setPendingLoginData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const badgeInputRef = useRef(null);
  const { caisse, initCaisse } = useCaisse();
  const storedCaisse = JSON.parse(localStorage.getItem("caisse_data"));
  const storedCaisseID = storedCaisse ? storedCaisse.id : null;
  useEffect(() => {
    if (isBadgeMode && badgeInputRef.current) {
      badgeInputRef.current.focus();
    }
  }, [isBadgeMode]);
  const [activeField, setActiveField] = useState("email"); // which field the keyboard types into
  const [token, setToken] = useToken();
  const [organisations, setOrganisations] = useOrganisations();
  const [activeJournal, setActiveJournal] = useActiveJournal();

  const navigate = useNavigate();
  const callImpression = async (dataTicket, type) => {
    console.log(dataTicket, dataTicket);
    const lines = ImpressionTicket(type, {
      ...dataTicket,
    });

    const finalBuffer = (() => {
      const allBytes = [];
      // treat `lines` as string
      const buf = iconv.encode(lines, "ISO-8859-1");
      allBytes.push(...buf);
      return new Uint8Array(allBytes);
    })();

    await PrintAction(finalBuffer, true);
  };
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Veuillez remplir tous les champs !");
      return;
    }

    setIsProcessing(true);
    try {
      const res = await login(email, password, null, storedCaisseID);
      console.log("Login response:", res);

      if (res?.data?.code === 200) {
        const token = res.data.data.token;
        const activeJournal = res.data.data.activeJournal;
        const user = res.data.data.user;

        // Check if this is a new journal with zero initial fund
        if (activeJournal && activeJournal.fond_initial === 0) {
          // Show modal to set initial fund
          setPendingLoginData({
            token,
            activeJournal,
            user,
            loginType: "email",
            credentials: { email, password },
          });
          setShowFundModal(true);
          setIsProcessing(false);
          return;
        }

        // Continue with normal login if journal already has funds
        completeLogin(token, activeJournal, user);
      } else {
        toast.error(
          res?.data?.errorMessage || "Email / mot de passe invalide !",
        );
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Erreur de connexion !");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBadgeLogin = async (badgeCode) => {
    if (!badgeCode) {
      toast.error("Veuillez entrer un code badge");
      return;
    }

    setIsProcessing(true);
    try {
      const res = await login(null, null, badgeCode, storedCaisseID);
      if (res?.data?.code === 200) {
        const token = res.data.data.token;
        const activeJournal = res.data.data.activeJournal;
        const user = res.data.data.user;

        // Check if this is a new journal with zero initial fund
        if (activeJournal && activeJournal.fond_initial === 0) {
          // Show modal to set initial fund
          setPendingLoginData({
            token,
            activeJournal,
            user,
            loginType: "badge",
            credentials: { badge: badgeCode },
          });
          setShowFundModal(true);
          setIsProcessing(false);
          return;
        }

        // Continue with normal login if journal already has funds
        completeLogin(token, activeJournal, user);
      } else {
        toast.error(res?.data?.errorMessage || "Badge invalide !");
      }
    } catch (error) {
      console.error("Badge login error:", error);
      toast.error("Erreur de connexion avec le badge !");
    } finally {
      setIsProcessing(false);
    }
  };

  const completeLogin = async (token, activeJournal, user) => {
    setToken(token);

    // Set active journal in localStorage
    if (activeJournal) {
      setActiveJournal(activeJournal);
      console.log("Active journal set:", activeJournal);
    }

    // Get user organisations
    const res2 = await getUserOrganisations(user.id);
    console.log("Organisations response:", res2?.data?.data);

    // Set organisations properly
    if (res2?.data?.data) {
      setOrganisations(res2.data.data);
      if (user.role === "caissier") {
        // Check if we already have a caisse in localStorage
        const storedCaisse = getCaisseFromStorage();

        if (!storedCaisse || storedCaisse.user_id !== user.id) {
          // Initialize new caisse
          await initCaisse(
            user,
            user.organisation_id,
            `Caisse_${user.prenom}_${user.nom}`,
          );
        }

        // Update activeJournal with caisse_id if available
        if (activeJournal && caisse) {
          await updateCashJournal(activeJournal.id, {
            caisse_id: caisse.id,
          });
        }
      }
      // Set redirect based on role
      const redirectTo =
        user.role === "caissier" || user.role === "serveur" ? "/caisse" : "/";
      setTimeout(() => navigate(redirectTo), 1000);
      toast.success("Connexion réussie ! Redirection...");
    } else {
      toast.error("Aucune organisation trouvée pour cet utilisateur");
    }
  };

  const handleFundModalSubmit = async () => {
    console.log("pendingLoginData", pendingLoginData);
    if (!pendingLoginData) {
      toast.error("Erreur interne : données manquantes");
      return;
    }

    const fundValue = parseFloat(fondInitial);

    if (isNaN(fundValue) || fundValue < 0) {
      toast.error("Veuillez entrer un montant valide");
      return;
    }

    setIsProcessing(true);

    try {
      const updateResponse = await updateCashJournal(
        pendingLoginData.activeJournal.id,
        {
          fond_initial: fundValue,
          fond_courant: fundValue,
        },
      );

      if (updateResponse?.data?.success !== "0") {
        const updatedJournal = updateResponse.data.data;

        await completeLogin(
          pendingLoginData.token,
          updatedJournal,
          pendingLoginData.user,
        );

        toast.success(`Fond initial défini à ${fundValue.toFixed(3)} TND`);
      } else {
        toast.error("Erreur lors de la configuration du journal");
      }
    } catch (error) {
      console.error("Error updating journal fund:", error);
      toast.error("Erreur lors de la configuration du journal");
    } finally {
      setShowFundModal(false);
      setFondInitial("");
      setPendingLoginData(null);
      setIsProcessing(false);
    }
  };

  // Fund modal keyboard handler (numeric context on Electron)
  const handleFundKeyPress = (key) => {
    if (isProcessing) return;
    if (key === "←") {
      setFondInitial((v) => v.slice(0, -1));
    } else if (key === "prix" || key === "✔") {
      handleFundModalSubmit();
    } else if (key === "." ) {
      if (!fondInitial.includes(".")) setFondInitial((v) => v + ".");
    } else if (/^\d$/.test(key)) {
      setFondInitial((v) => v + key);
    }
    // ignore %, -
  };

  // Badge mode keyboard handler
  const handleBadgeKeyPress = (key) => {
    if (isProcessing) return;
    if (key === "←") {
      setPin(pin.slice(0, -1));
    } else if (key === "✔") {
      handleBadgeLogin(pin);
    } else if (pin.length < 8) {
      setPin(pin + key);
    }
  };

  // Email/password mode keyboard handler
  const handleEmailKeyPress = (key) => {
    if (isProcessing) return;
    if (key === "←") {
      if (activeField === "email") setEmail((v) => v.slice(0, -1));
      else setPassword((v) => v.slice(0, -1));
    } else if (key === "✔") {
      // ✔ on email field → move to password; on password → submit
      if (activeField === "email") {
        setActiveField("password");
      } else {
        handleLogin({ preventDefault: () => {} });
      }
    } else {
      // letters come as uppercase from the keyboard; lowercase them for email/password
      const char = key.toLowerCase();
      if (activeField === "email") setEmail((v) => v + char);
      else setPassword((v) => v + char);
    }
  };

  const handleModalClose = () => {
    if (!isProcessing) {
      setShowFundModal(false);
      setFondInitial("");
      setPendingLoginData(null);
      // Clear the PIN if it was a badge login that was cancelled
      if (pendingLoginData?.loginType === "badge") {
        setPin("");
      }
    }
  };

  return (
    <div className={`login-container ${isBadgeMode ? "badge-mode" : ""}`}>
      <ToastContainer />

      {/* Fond Initial Modal */}
      <Modal
        open={showFundModal}
        onClose={handleModalClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: 520,
            bgcolor: "#20304c",
            borderRadius: 3,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom sx={{ color: "#ebdeff", fontWeight: 700 }}>
            Configuration du Journal de Caisse
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: "rgba(235,222,255,0.5)" }}>
            Définissez le fond initial de départ.
          </Typography>

          <TextField
            fullWidth
            label="Fond Initial (TND)"
            type="text"
            value={fondInitial}
            placeholder="0.000"
            inputProps={{
              readOnly: true,
              inputMode: "none",
              step: "0.001",
              min: "0",
            }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                color: "#ebdeff",
                "& fieldset": { borderColor: "rgba(235,222,255,0.15)" },
                "&:hover fieldset": { borderColor: "rgba(253,104,62,0.4)" },
                "&.Mui-focused fieldset": { borderColor: "#fd683e" },
              },
              "& .MuiInputLabel-root": { color: "rgba(235,222,255,0.45)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#fd683e" },
              "& input": { cursor: "pointer" },
            }}
            disabled={isProcessing}
          />

          <Box sx={{ mb: 2 }}>
            <KeyboardComponent
              context="numeric"
              onKeyPress={handleFundKeyPress}
              showLetters={false}
              setShowLetters={() => {}}
              disabled={isProcessing}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              onClick={handleModalClose}
              disabled={isProcessing}
              sx={{ color: "rgba(235,222,255,0.5)" }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleFundModalSubmit}
              variant="contained"
              disabled={isProcessing || !fondInitial}
              sx={{
                background: "linear-gradient(135deg, #fd683e, #6d28d9)",
                "&:hover": { opacity: 0.88 },
                "&.Mui-disabled": { opacity: 0.4, color: "#fff" },
              }}
            >
              {isProcessing ? "Traitement..." : "Confirmer"}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* LEFT SIDE */}
      <div className="login-left">
        {!isBadgeMode ? (
          <div className="login-brand">
            <div className="brand-logo">Scaniny</div>
            <div className="brand-sub">Système de caisse</div>
            <div className="brand-tagline">
              Connectez-vous pour accéder à votre espace de gestion
            </div>
          </div>
        ) : (
          <div className="badge-form">
            <h2>Badge Login</h2>
            <p>Entrez votre code badge pour continuer</p>
            <input
              type="text"
              ref={badgeInputRef}
              value={pin}
              className="pin-display"
              placeholder="• • • • • • • •"
              readOnly
              inputMode="none"
              disabled={isProcessing}
            />
            {isProcessing && (
              <div style={{ marginTop: "14px", color: "rgba(235,222,255,0.45)", fontSize: "14px" }}>
                Traitement en cours...
              </div>
            )}
          </div>
        )}
      </div>

      {/* RIGHT SIDE */}
      <div className="login-right">
        {!isBadgeMode ? (
          <>
            <div className="form-wrapper">
              <h2>Connexion</h2>

              <div
                className={`login-field${isElectron && activeField === "email" ? " active" : ""}`}
                onClick={() => isElectron && setActiveField("email")}
              >
                <i className="ti-user field-icon" />
                <input
                  type="text"
                  placeholder="Adresse e-mail"
                  value={email}
                  readOnly={isElectron}
                  inputMode={isElectron ? "none" : undefined}
                  onChange={isElectron ? undefined : (e) => setEmail(e.target.value)}
                  disabled={isProcessing}
                />
              </div>

              <div
                className={`login-field${isElectron && activeField === "password" ? " active" : ""}`}
                onClick={() => isElectron && setActiveField("password")}
              >
                <i className="ti-lock field-icon" />
                <input
                  type="password"
                  placeholder="Mot de passe"
                  value={password}
                  readOnly={isElectron}
                  inputMode={isElectron ? "none" : undefined}
                  onChange={isElectron ? undefined : (e) => setPassword(e.target.value)}
                  disabled={isProcessing}
                />
              </div>

              {!isElectron && (
                <>
                  <div className="form-options">
                    <label style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <input type="checkbox" /> Souviens-toi de moi
                    </label>
                    <Link to="/forgot-password" className="forgot-link">
                      Mot de passe oublié ?
                    </Link>
                  </div>
                  <button
                    type="button"
                    className="btn-submit"
                    onClick={handleLogin}
                    disabled={isProcessing}
                  >
                    {isProcessing ? "Connexion..." : "Continuer"}
                  </button>
                </>
              )}

              <p className="signup-text">
                Pas encore de compte ?{" "}
                <Link to="/onboarding">S'inscrire</Link>
              </p>
            </div>

            {isElectron && (
              <KeyboardComponent
                onKeyPress={handleEmailKeyPress}
                showLetters={showLetters}
                setShowLetters={setShowLetters}
                disabled={isProcessing}
                context="login"
              />
            )}
          </>
        ) : (
          <KeyboardComponent
            onKeyPress={handleBadgeKeyPress}
            showLetters={showLetters}
            setShowLetters={setShowLetters}
            disabled={isProcessing}
            context="login"
          />
        )}

        <div className="mode-switch">
          <label className="switch">
            <input
              type="checkbox"
              checked={isBadgeMode}
              onChange={() => {
                if (!isProcessing) {
                  setPin("");
                  setActiveField("email");
                  setIsBadgeMode(!isBadgeMode);
                }
              }}
              disabled={isProcessing}
            />
            <span className="slider"></span>
          </label>
          <span className="switch-label">
            {isBadgeMode ? "Badge" : "Email"}
          </span>
        </div>
      </div>
    </div>
  );
}

export default Login;
